import type {
  Asset,
  DemoState,
  FamilyAAlgorithm,
  MethodSnapshot,
  MethodSnapshotA,
  MethodSnapshotB,
  PatternAssetId,
  RegionEntry,
  RegionStack,
  StampMaskAssetId,
  StatePatch,
} from '../state';
import { registerAsset } from './asset-registry';
import type { ToneMethodField } from './tone-method-fields';

/**
 * The subset of DemoState that represents dial-able per-method parameters.
 * Only these fields are eligible for bulk-region patching via applyMethodPartial.
 *
 * Excluded from eligibility (remain on global state):
 *   - source / sourceOriginal / sourceBrightness / sourceContrast - source-image adjustments
 *   - patternImage / customStampMask - binary asset slots (uploaded ImageData)
 *   - labelMap / regions / regionEntries / regionStacks - segmentation structure
 *   - assetLibrary / assetData / assetDataAccessOrder - asset registry
 *   - regionLabelPlane - compact label plane
 *   - activeRegionUuids / showRegionOutline - UI / view state (showRegionOutline deprecated, always false)
 *   - labelmapError - error state
 *   - grayscaleMode - not a method param
 *
 * The test: if the field appears in MethodSnapshot, it is eligible here.
 */
export type MethodPartial = Partial<Pick<DemoState, ToneMethodField>>;

// ---------------------------------------------------------------------------
// buildMethodSnapshot - materialise a MethodSnapshot from current flat state
// ---------------------------------------------------------------------------

/**
 * Find a pattern asset ID in the library by looking up the current
 * patternImage binary in assetData (reverse lookup by content identity).
 * Returns the first matching PatternAssetId or null.
 *
 * Pure function - no DOM, no side effects.
 */
function findPatternRefId(state: DemoState): PatternAssetId | null {
  if (!state.patternImage) return null;
  for (const asset of state.assetLibrary) {
    if (asset.kind !== 'pattern') continue;
    const bin = state.assetData.get(asset.id);
    if (bin === state.patternImage) return asset.id as PatternAssetId;
  }
  return null;
}

/**
 * Result of registering a stamp mask asset alongside snapshot building.
 * Carries the refId to embed in the snapshot and (if newly registered)
 * the updated library + data so the caller can include them in the patch.
 */
type StampMaskRegistration = {
  stampMaskRefId: StampMaskAssetId;
  assetLibrary: Asset[];
  assetData: Map<string, ImageData>;
  assetDataAccessOrder: string[];
};

type PatternRegistration = {
  patternRefId: PatternAssetId;
  assetLibrary: Asset[];
  assetData: Map<string, ImageData>;
  assetDataAccessOrder: string[];
};

/**
 * Register the current global customStampMask (if any) into the asset
 * library and return the refId plus any updated asset collections.
 * Returns null when stampType !== 'custom' or customStampMask is null.
 *
 * Pure function - no DOM, no side effects.
 */
function registerStampMaskIfNeeded(
  state: DemoState,
  partial: MethodPartial,
): StampMaskRegistration | null {
  const targetStampType = partial.stampType ?? state.stampType;
  const targetPatternHalftoneStampType =
    partial.patternHalftoneStampType ?? state.patternHalftoneStampType;
  const needsStampMask =
    targetStampType === 'custom' || targetPatternHalftoneStampType === 'custom';
  if (!needsStampMask || !state.customStampMask) return null;
  const result = registerAsset(
    state.assetLibrary,
    state.assetData,
    state.assetDataAccessOrder,
    state.customStampMask,
    'stampMask',
  );
  return {
    stampMaskRefId: result.asset.id as StampMaskAssetId,
    assetLibrary: result.assetLibrary,
    assetData: result.assetData,
    assetDataAccessOrder: result.assetDataAccessOrder,
  };
}

function registerPatternIfNeeded(
  state: DemoState,
  targetFamily: DemoState['family'],
): PatternRegistration | null {
  if (targetFamily !== 'B' || !state.patternImage) return null;
  const result = registerAsset(
    state.assetLibrary,
    state.assetData,
    state.assetDataAccessOrder,
    state.patternImage,
    'pattern',
  );
  return {
    patternRefId: result.asset.id as PatternAssetId,
    assetLibrary: result.assetLibrary,
    assetData: result.assetData,
    assetDataAccessOrder: result.assetDataAccessOrder,
  };
}

/**
 * Materialise a MethodSnapshot representing the current global DemoState.
 *
 * The snapshot carries `version: 1` and copies all dial-able parameters
 * from the flat state into the appropriate discriminated-union shape.
 * Binary assets (ImageData) are replaced by their asset-library refIDs
 * where available; missing or un-uploaded assets become null.
 *
 * Pure function - no DOM, no side effects.
 *
 * @param stampMaskRefIdOverride - When provided, this refId is embedded in the
 *   snapshot instead of null. The caller is responsible for registering the
 *   asset before calling this function. Used by `applyMethodPartial` to
 *   propagate custom stamp masks into freshly materialised overrides.
 * @param patternRefIdOverride - When provided, this refId is embedded in
 *   Family B snapshots. Callers pass this after registering the live pattern
 *   image so LRU eviction cannot make new overrides lose their pattern.
 */
export function buildMethodSnapshot(
  state: DemoState,
  stampMaskRefIdOverride?: StampMaskAssetId | null,
  patternRefIdOverride?: PatternAssetId | null,
): MethodSnapshot {
  // Use the provided refId when given; otherwise null. The caller pre-registers
  // the asset so this function itself stays side-effect-free.
  const resolvedStampMaskRefId = stampMaskRefIdOverride ?? null;
  const resolvedPatternRefId =
    patternRefIdOverride !== undefined
      ? patternRefIdOverride
      : findPatternRefId(state);

  switch (state.family) {
    case 'A': {
      const snap: MethodSnapshotA = {
        version: 1,
        family: 'A',
        cellSize: state.cellSize,
        stampType: state.stampType,
        stampCoverage: state.stampCoverage,
        polarity: state.polarity,
        density: state.density,
        blackThreshold: state.blackThreshold,
        whiteThreshold: state.whiteThreshold,
        gridAngleDeg: state.gridAngleDeg,
        shapeAngleDeg: state.shapeAngleDeg,
        shapeFlipX: state.shapeFlipX,
        shapeFlipY: state.shapeFlipY,
        algorithmJitter: state.algorithmJitter,
        patternNoiseScale: state.patternNoiseScale,
        stampMaskRefId: resolvedStampMaskRefId,
        patternRefId: null,
      };
      return snap;
    }

    case 'B': {
      const snap: MethodSnapshotB = {
        version: 1,
        family: 'B',
        isGrayscale: state.isGrayscale,
        blurRadius: state.blurRadius,
        patternBrightness: state.patternBrightness,
        patternContrast: state.patternContrast,
        polarity: state.polarity,
        density: state.density,
        blendMode: state.patternBlendMode,
        patternScale: state.patternScale,
        patternRotationDeg: state.patternRotationDeg,
        patternFlipX: state.patternFlipX,
        patternFlipY: state.patternFlipY,
        patternNoise: state.patternNoise,
        patternNoiseScale: state.patternNoiseScale,
        patternHalftoneEnabled: state.patternHalftoneEnabled,
        patternHalftoneCellSize: state.patternHalftoneCellSize,
        patternHalftoneStampType: state.patternHalftoneStampType,
        patternHalftoneStampCoverage: state.patternHalftoneStampCoverage,
        patternHalftonePolarity: state.patternHalftonePolarity,
        patternHalftoneDensity: state.patternHalftoneDensity,
        patternHalftoneBlackThreshold: state.patternHalftoneBlackThreshold,
        patternHalftoneWhiteThreshold: state.patternHalftoneWhiteThreshold,
        patternHalftoneGridAngleDeg: state.patternHalftoneGridAngleDeg,
        patternHalftoneShapeAngleDeg: state.patternHalftoneShapeAngleDeg,
        patternHalftoneShapeFlipX: state.patternHalftoneShapeFlipX,
        patternHalftoneShapeFlipY: state.patternHalftoneShapeFlipY,
        patternHalftoneAlgorithmJitter: state.patternHalftoneAlgorithmJitter,
        patternHalftoneNoiseScale: state.patternNoiseScale,
        patternInvert: state.patternInvert,
        patternOpacity: state.patternOpacity,
        blackThreshold: state.blackThreshold,
        whiteThreshold: state.whiteThreshold,
        patternRefId: resolvedPatternRefId,
        stampMaskRefId:
          state.patternHalftoneStampType === 'custom' ? resolvedStampMaskRefId : null,
      };
      return snap;
    }

    case 'C': {
      const algorithm: FamilyAAlgorithm = state.algorithmA;
      const common = {
        version: 1 as const,
        family: 'C' as const,
        cellSize: state.cellSize,
        stampType: state.stampType,
        stampCoverage: state.stampCoverage,
        polarity: state.polarity,
        density: state.density,
        blackThreshold: state.blackThreshold,
        whiteThreshold: state.whiteThreshold,
        gridAngleDeg: state.gridAngleDeg,
        shapeAngleDeg: state.shapeAngleDeg,
        shapeFlipX: state.shapeFlipX,
        shapeFlipY: state.shapeFlipY,
        algorithmJitter: state.algorithmJitter,
        grainStrength: state.grainStrength,
        grainScale: state.grainScale,
        stampMaskRefId: resolvedStampMaskRefId,
        patternRefId: null as null,
      };
      switch (algorithm) {
        case 'bayer':
          return { ...common, algorithm: 'bayer', matrixSize: state.matrixSize };
        case 'bluenoise':
          return { ...common, algorithm: 'bluenoise' };
        case 'floyd-steinberg':
          return {
            ...common,
            algorithm: 'floyd-steinberg',
            serpentine: state.serpentine,
            strength: state.fsStrength,
          };
        case 'atkinson':
          return { ...common, algorithm: 'atkinson', strength: state.atkinsonStrength };
        case 'werness':
          return {
            ...common,
            algorithm: 'werness',
            brightness: state.brightness,
            contrast: state.contrast,
          };
      }
    }
  }
}

// ---------------------------------------------------------------------------
// patchSnapshotWithMethodPartial - merge MethodPartial into a MethodSnapshot
// ---------------------------------------------------------------------------

/**
 * Apply a MethodPartial onto an existing MethodSnapshot, returning an updated
 * snapshot. Family changes are handled by replacing the entire snapshot shape.
 *
 * When partial.family differs from the snapshot's current family, the snapshot
 * is rebuilt by merging from the base state first, then the partial's specific
 * fields take effect.
 *
 * @param stampMaskRefId - When non-null, this refId is forwarded into every
 *   freshly-materialised snapshot during family-rebuild or algo-rebuild paths.
 *   Callers that pre-register a custom stamp mask pass the resulting refId here
 *   so the mask survives family and algorithm changes.
 *
 * Pure function - no DOM, no side effects.
 */
function patchSnapshotWithPartial(
  snapshot: MethodSnapshot,
  partial: MethodPartial,
  baseState: DemoState,
  stampMaskRefId: StampMaskAssetId | null = null,
  patternRefId: PatternAssetId | null = null,
): MethodSnapshot {
  // If the family is changing, start from a fresh snapshot of the new family.
  const targetFamily = partial.family ?? snapshot.family;
  if (targetFamily !== snapshot.family) {
    // Materialise a snapshot for the new family from base state, then apply
    // any non-family fields from partial. Omit `family` from the recursive
    // call so exactOptionalPropertyTypes is satisfied (assigning `undefined`
    // to a required key is not the same as omitting it).
    const stateWithFamily: DemoState = { ...baseState, family: targetFamily };
    // Thread stampMaskRefId into the fresh snapshot so the custom mask is preserved.
    const freshSnap = buildMethodSnapshot(stateWithFamily, stampMaskRefId, patternRefId);
    const { family: _omitFamily, ...partialWithoutFamily } = partial;
    return patchSnapshotWithPartial(
      freshSnap,
      partialWithoutFamily,
      baseState,
      stampMaskRefId,
      patternRefId,
    );
  }

  // Same family - patch in-place field by field.
  switch (snapshot.family) {
    case 'A': {
      const s = snapshot;
      return {
        ...s,
        cellSize: partial.cellSize ?? s.cellSize,
        stampType: partial.stampType ?? s.stampType,
        stampCoverage: partial.stampCoverage ?? s.stampCoverage,
        polarity: partial.polarity ?? s.polarity,
        density: partial.density ?? s.density,
        blackThreshold: partial.blackThreshold ?? s.blackThreshold,
        whiteThreshold: partial.whiteThreshold ?? s.whiteThreshold,
        gridAngleDeg: partial.gridAngleDeg ?? s.gridAngleDeg,
        shapeAngleDeg: partial.shapeAngleDeg ?? s.shapeAngleDeg,
        shapeFlipX: partial.shapeFlipX ?? s.shapeFlipX ?? false,
        shapeFlipY: partial.shapeFlipY ?? s.shapeFlipY ?? false,
        algorithmJitter: partial.algorithmJitter ?? s.algorithmJitter,
        patternNoiseScale:
          partial.patternNoiseScale ?? s.patternNoiseScale ?? baseState.patternNoiseScale,
      };
    }

    case 'B': {
      const s = snapshot;
      return {
        ...s,
        isGrayscale: partial.isGrayscale ?? s.isGrayscale,
        blurRadius: partial.blurRadius ?? s.blurRadius,
        patternBrightness: partial.patternBrightness ?? s.patternBrightness,
        patternContrast: partial.patternContrast ?? s.patternContrast,
        polarity: partial.polarity ?? s.polarity,
        density: partial.density ?? s.density,
        blendMode: partial.patternBlendMode ?? s.blendMode,
        patternScale: partial.patternScale ?? s.patternScale,
        patternRotationDeg: partial.patternRotationDeg ?? s.patternRotationDeg ?? 0,
        patternFlipX: partial.patternFlipX ?? s.patternFlipX ?? false,
        patternFlipY: partial.patternFlipY ?? s.patternFlipY ?? false,
        patternNoise: partial.patternNoise ?? s.patternNoise,
        patternNoiseScale: partial.patternNoiseScale ?? s.patternNoiseScale,
        patternHalftoneEnabled:
          partial.patternHalftoneEnabled ?? s.patternHalftoneEnabled ?? false,
        patternHalftoneCellSize:
          partial.patternHalftoneCellSize ?? s.patternHalftoneCellSize ?? 8,
        patternHalftoneStampType:
          partial.patternHalftoneStampType ?? s.patternHalftoneStampType ?? 'circle',
        patternHalftoneStampCoverage:
          partial.patternHalftoneStampCoverage ?? s.patternHalftoneStampCoverage ?? 0.9,
        patternHalftonePolarity:
          partial.patternHalftonePolarity ?? s.patternHalftonePolarity ?? 'black',
        patternHalftoneDensity:
          partial.patternHalftoneDensity ?? s.patternHalftoneDensity ?? 50,
        patternHalftoneBlackThreshold:
          partial.patternHalftoneBlackThreshold ?? s.patternHalftoneBlackThreshold ?? 0,
        patternHalftoneWhiteThreshold:
          partial.patternHalftoneWhiteThreshold ?? s.patternHalftoneWhiteThreshold ?? 1,
        patternHalftoneGridAngleDeg:
          partial.patternHalftoneGridAngleDeg ?? s.patternHalftoneGridAngleDeg ?? 0,
        patternHalftoneShapeAngleDeg:
          partial.patternHalftoneShapeAngleDeg ?? s.patternHalftoneShapeAngleDeg ?? 0,
        patternHalftoneShapeFlipX:
          partial.patternHalftoneShapeFlipX ?? s.patternHalftoneShapeFlipX ?? false,
        patternHalftoneShapeFlipY:
          partial.patternHalftoneShapeFlipY ?? s.patternHalftoneShapeFlipY ?? false,
        patternHalftoneAlgorithmJitter:
          partial.patternHalftoneAlgorithmJitter ?? s.patternHalftoneAlgorithmJitter ?? 0.075,
        patternHalftoneNoiseScale:
          partial.patternHalftoneNoiseScale ??
          partial.patternNoiseScale ??
          s.patternNoiseScale ??
          s.patternHalftoneNoiseScale ??
          1,
        stampMaskRefId:
          (partial.patternHalftoneStampType ?? s.patternHalftoneStampType) === 'custom'
            ? stampMaskRefId ?? s.stampMaskRefId
            : null,
        patternRefId:
          partial.family === 'B' && patternRefId !== null
            ? patternRefId
            : s.patternRefId,
        patternInvert: partial.patternInvert ?? s.patternInvert,
        patternOpacity: partial.patternOpacity ?? s.patternOpacity,
        blackThreshold: partial.blackThreshold ?? s.blackThreshold,
        whiteThreshold: partial.whiteThreshold ?? s.whiteThreshold,
      };
    }

    case 'C': {
      const s = snapshot;
      // Common C fields shared across all algorithms.
      const commonPatch = {
        cellSize: partial.cellSize ?? s.cellSize,
        stampType: partial.stampType ?? s.stampType,
        stampCoverage: partial.stampCoverage ?? s.stampCoverage,
        polarity: partial.polarity ?? s.polarity,
        density: partial.density ?? s.density,
        blackThreshold: partial.blackThreshold ?? s.blackThreshold,
        whiteThreshold: partial.whiteThreshold ?? s.whiteThreshold,
        gridAngleDeg: partial.gridAngleDeg ?? s.gridAngleDeg,
        shapeAngleDeg: partial.shapeAngleDeg ?? s.shapeAngleDeg,
        shapeFlipX: partial.shapeFlipX ?? s.shapeFlipX ?? false,
        shapeFlipY: partial.shapeFlipY ?? s.shapeFlipY ?? false,
        algorithmJitter: partial.algorithmJitter ?? s.algorithmJitter,
        grainStrength: partial.grainStrength ?? s.grainStrength ?? baseState.grainStrength,
        grainScale: partial.grainScale ?? s.grainScale ?? baseState.grainScale,
      };

      // Algorithm change within Family C: replace discriminant + algorithm-specific fields.
      const newAlgorithm: FamilyAAlgorithm = (partial.algorithmA as FamilyAAlgorithm | undefined) ?? s.algorithm;
      if (newAlgorithm !== s.algorithm) {
        // Build a fresh C snapshot for the new algorithm from baseState.
        // Thread stampMaskRefId so the custom mask is preserved across algo changes.
        const stateWithAlgo: DemoState = { ...baseState, family: 'C', algorithmA: newAlgorithm };
        const freshC = buildMethodSnapshot(stateWithAlgo, stampMaskRefId, patternRefId);
        // Re-apply the common patch fields; omit `algorithmA` so it is not
        // recursed again (exactOptionalPropertyTypes: omitting != undefined).
        const { algorithmA: _omitAlgo, ...partialWithoutAlgo } = partial;
        return patchSnapshotWithPartial(
          freshC,
          partialWithoutAlgo,
          baseState,
          stampMaskRefId,
          patternRefId,
        );
      }

      // Same algorithm - patch algorithm-specific fields.
      switch (s.algorithm) {
        case 'bayer':
          return {
            ...s,
            ...commonPatch,
            matrixSize: (partial.matrixSize as 2 | 4 | 8 | 16 | undefined) ?? s.matrixSize,
          };
        case 'bluenoise':
          return { ...s, ...commonPatch };
        case 'floyd-steinberg':
          return {
            ...s,
            ...commonPatch,
            serpentine: partial.serpentine ?? s.serpentine,
            strength: partial.fsStrength ?? s.strength,
          };
        case 'atkinson':
          return {
            ...s,
            ...commonPatch,
            strength: partial.atkinsonStrength ?? s.strength,
          };
        case 'werness':
          return {
            ...s,
            ...commonPatch,
            brightness: partial.brightness ?? s.brightness,
            contrast: partial.contrast ?? s.contrast,
          };
      }
    }
  }
}

// ---------------------------------------------------------------------------
// applyMethodPartial - main dispatch
// ---------------------------------------------------------------------------

/**
 * Compute the StatePatch that should be applied to Store in response to a
 * method-control change.
 *
 * Three modes:
 *
 * 0. Active stack (Item 32 - v0.2.4.3): activeStackUuid is non-null. Write the
 *    patch to the named stack's `override` field. Per-entry overrides remain
 *    untouched; they compose on top of the stack override at render time.
 *    - If the stack's override is null, materialise a fresh MethodSnapshot
 *      from the current global state first, then apply the partial on top.
 *    - If override already exists, patch it in place.
 *    Return `{ regionStacks: nextStacks }` only - do NOT spread `partial` into
 *    the patch. Spreading it would leak the edit into global state, where the
 *    renderer's `buildMethod(state)` fallback uses it for any region whose
 *    composed override is null (ungrouped regions, regions in other stacks).
 *    Sliders stay in sync via `syncVisibility`, which reads from the active
 *    stack's override snapshot when `activeStackUuid` is set.
 *    If `activeStackUuid` points to a stack that no longer exists, fall
 *    through to Mode 1/2 below so the user's edit is not silently dropped.
 *
 * 1. No active region (activeRegionUuids is empty - single-image / virtual
 *    Default mode): return `partial` directly. The global state is the only
 *    target; no region overrides involved.
 *
 * 2. Active regions present: for every UUID in activeRegionUuids, locate the
 *    matching RegionEntry and update its override:
 *    - If override is null, materialise a fresh MethodSnapshot from the
 *      current global state first (so the region "inherits" the current
 *      controls as a baseline), then apply the partial on top.
 *    - If override already exists, patch it in place.
 *    Return `{ regionEntries: nextEntries }` only - do NOT spread `partial`
 *    into the patch. Spreading it would leak the edit into global state,
 *    where the renderer's `buildMethod(state)` fallback uses it for any
 *    region whose composed override is null (ungrouped regions, regions
 *    with no override). Sliders stay in sync via `effectiveDialValues` in
 *    controls.ts, which reads from the active region's override snapshot.
 *
 * Pure function - no DOM, no side effects, no console.log.
 */
export function applyMethodPartial(state: DemoState, partial: MethodPartial): StatePatch {
  // Pre-register the custom stamp mask (if any) once before any snapshot is
  // materialised. The refId is embedded in every freshly-created override
  // snapshot so custom stamps survive per-area and per-stack overrides.
  // Existing overrides are not mutated here - they already carry their own
  // stampMaskRefId (or null) and patchSnapshotWithPartial preserves it.
  const stampMaskReg = registerStampMaskIfNeeded(state, partial);
  const stampMaskRefId = stampMaskReg?.stampMaskRefId ?? null;
  const stateAfterStampMask: DemoState = stampMaskReg
    ? {
        ...state,
        assetLibrary: stampMaskReg.assetLibrary,
        assetData: stampMaskReg.assetData,
        assetDataAccessOrder: stampMaskReg.assetDataAccessOrder,
      }
    : state;
  const targetSnapshotFamily = partial.family ?? state.family;
  const patternReg = registerPatternIfNeeded(stateAfterStampMask, targetSnapshotFamily);
  const patternRefId = patternReg?.patternRefId ?? null;
  // Use the potentially-updated asset collections so the refId resolves.
  const assetLibraryForPatch =
    patternReg?.assetLibrary ?? stampMaskReg?.assetLibrary ?? state.assetLibrary;
  const assetDataForPatch = patternReg?.assetData ?? stampMaskReg?.assetData ?? state.assetData;
  const assetDataAccessOrderForPatch =
    patternReg?.assetDataAccessOrder ??
    stampMaskReg?.assetDataAccessOrder ??
    state.assetDataAccessOrder;

  // Build a state view with the updated asset collections so that
  // patchSnapshotWithPartial (and any recursive buildMethodSnapshot calls
  // inside it for family changes) operates on up-to-date asset data.
  const stateWithAssets: DemoState = stampMaskReg !== null || patternReg !== null
    ? {
        ...state,
        assetLibrary: assetLibraryForPatch,
        assetData: assetDataForPatch,
        assetDataAccessOrder: assetDataAccessOrderForPatch,
      }
    : state;

  // Asset delta to include in the patch when new assets were registered.
  // Include when a live asset was registered or touched so LRU order stays
  // current even on dedup hits.
  const assetDelta: StatePatch = stampMaskReg !== null || patternReg !== null
    ? {
        assetLibrary: assetLibraryForPatch,
        assetData: assetDataForPatch,
        assetDataAccessOrder: assetDataAccessOrderForPatch,
      }
    : {};

  // Mode 0 (Item 32): active stack - update the stack's override.
  if (state.activeStackUuid !== null) {
    const stackUuid = state.activeStackUuid;
    const targetStack = state.regionStacks.find((s) => s.uuid === stackUuid);

    if (targetStack !== undefined) {
      const nextStacks: RegionStack[] = state.regionStacks.map((stack) => {
        if (stack.uuid !== stackUuid) return stack;

        // Materialise or patch the stack override snapshot.
        // When materialising fresh, pass the stamp mask refId so it is
        // embedded in the snapshot from the start.
        const baseSnapshot: MethodSnapshot =
          stack.override !== null
            ? stack.override
            : buildMethodSnapshot(stateWithAssets, stampMaskRefId, patternRefId);

        const nextOverride = patchSnapshotWithPartial(
          baseSnapshot,
          partial,
          stateWithAssets,
          stampMaskRefId,
          patternRefId,
        );

        return { ...stack, override: nextOverride };
      });

      // Do NOT spread partial into the return - that would write the patch
      // fields back into global state, causing unrelated regions (ungrouped
      // or in other stacks with no override) to inherit the edit.
      // Include asset delta when new assets were registered during materialisation.
      return { ...assetDelta, regionStacks: nextStacks };
    }

    // Stale activeStackUuid - the referenced stack no longer exists.
    // Fall through to per-region / global modes so the user's edit is
    // not silently dropped.
  }

  // Mode 1: no labelmap / virtual Default - patch global state directly.
  if (state.activeRegionUuids.length === 0) {
    return { ...assetDelta, ...partial };
  }

  // Mode 2: active regions - update each selected region's override.
  const activeSet = new Set(state.activeRegionUuids);

  const nextEntries: RegionEntry[] = state.regionEntries.map((entry) => {
    if (!activeSet.has(entry.uuid)) return entry;

    // Materialise or patch the override snapshot.
    // When materialising fresh, pass the stamp mask refId so it is
    // embedded in the snapshot from the start.
    const baseSnapshot: MethodSnapshot =
      entry.override !== null
        ? entry.override
        : buildMethodSnapshot(stateWithAssets, stampMaskRefId, patternRefId);

    const nextOverride = patchSnapshotWithPartial(
      baseSnapshot,
      partial,
      stateWithAssets,
      stampMaskRefId,
      patternRefId,
    );

    return { ...entry, override: nextOverride };
  });

  // Do NOT spread partial into the return - that would write the patch
  // fields back into global state, causing unrelated regions (without a
  // per-region override) to inherit the edit via the buildMethod(state)
  // global fallback at render time.
  // Sliders stay in sync via syncVisibility / effectiveDialValues in
  // controls.ts, which reads from the active region's override snapshot
  // when activeRegionUuids is non-empty.
  // Include asset delta when new assets were registered during materialisation.
  return { ...assetDelta, regionEntries: nextEntries };
}
